import { useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/stores/hook";
import { setCurrentLeadId } from "@/features/leads/stores/leadFunctionalSlice";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { AlarmClock, ExternalLink, HelpCircle } from "lucide-react";
import axiosInstance from "@/lib/axios";
import { notificationEndpoint } from "@/config/endpoints";
import { queryClient } from "@/lib/queryClient";
import {
  SHUFFLE_WARNINGS_KEY,
  patchNotificationsRead,
} from "../api/useNotifications";
import type { ServerNotification } from "../types";

/**
 * Pre-shuffle warning modal — pops LIVE (not just at login) when the server
 * warns that some of the user's leads will be reshuffled at the next sweep
 * tick (~15 min). Acting on a lead resets its clocks and keeps it, so this
 * modal is the "save your leads" nudge: it lists exactly which leads, when,
 * and why (the automation's condition in plain English).
 *
 * Liveness: the socket NOTIFICATION handler refetches `SHUFFLE_WARNINGS_KEY`
 * ONLY when the pushed row is itself an `automation_shuffle_warning` — this
 * query no longer shares the bell's `["notifications"]` prefix, so the
 * ordinary stage/note/call pushes (~55 recipients each) never touch it.
 * Any unread warning card whose id we haven't acknowledged this session
 * opens the modal. Closing marks the cards read (DB-backed, cross-device) —
 * they stay in the bell as read.
 */

const WARNING_TYPE = "automation_shuffle_warning";

export default function ShuffleWarningModal() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthed = !!useAppSelector((s) => (s.auth as any)?.currentUser);

  const seenIdsRef = useRef<Set<number>>(new Set());
  const [dismissedIds, setDismissedIds] = useState<Set<number>>(new Set());

  const { data } = useQuery({
    queryKey: SHUFFLE_WARNINGS_KEY,
    queryFn: async () => {
      const { data } = await axiosInstance.get(notificationEndpoint, {
        // `withCount=0`: the bell owns the unread badge; skip the scoped
        // count query on the server for this side list.
        params: { type: WARNING_TYPE, unreadOnly: "true", limit: "10", withCount: "0" },
      });
      return (data?.data ?? []) as ServerNotification[];
    },
    enabled: isAuthed,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  const cards = useMemo(
    () => (data ?? []).filter((c) => !dismissedIds.has(c.id)),
    [data, dismissedIds]
  );
  const hasUnseen = cards.some((c) => !seenIdsRef.current.has(c.id));
  const open = cards.length > 0 && hasUnseen;
  if (open) for (const c of cards) seenIdsRef.current.add(c.id);

  async function acknowledge() {
    setDismissedIds((prev) => {
      const next = new Set(prev);
      for (const c of cards) next.add(c.id);
      return next;
    });
    const ids = cards.map((c) => c.id);
    await Promise.allSettled(
      ids.map((id) => axiosInstance.patch(`${notificationEndpoint}/${id}/read`))
    );
    // Patch the bell + this list in place — no list GET; the server's own
    // `read` push to this user re-applies the same patch (idempotent).
    patchNotificationsRead(ids);
    queryClient.setQueryData<ServerNotification[]>(SHUFFLE_WARNINGS_KEY, (old) =>
      old ? old.filter((c) => !ids.includes(c.id)) : old
    );
  }

  function openLead(leadId: number) {
    void acknowledge();
    window.setTimeout(() => {
      dispatch(setCurrentLeadId(leadId));
      if (!location.pathname.startsWith("/leads")) navigate("/leads");
    }, 16);
  }

  if (!open) return null;

  const totalLeads = cards.reduce((s, c) => s + (c.payload.count ?? 0), 0);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && void acknowledge()}>
      <DialogContent className="sm:max-w-[440px] max-h-[75vh] p-0 rounded-[20px] border-amber-200 shadow-[0px_8px_40px_rgba(0,0,0,0.14)] gap-0 overflow-hidden flex flex-col [&>button]:hidden">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 pt-5 pb-4 border-b border-amber-100 bg-amber-50/60 shrink-0">
          <div className="size-10 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center">
            <AlarmClock size={18} className="text-amber-600" />
          </div>
          <div>
            <h2 className="text-[16px] font-semibold text-[#051912] tracking-[-0.32px] leading-tight">
              {totalLeads} lead{totalLeads === 1 ? "" : "s"} about to be reshuffled
            </h2>
            <p className="text-[12px] text-[#697571] mt-0.5">
              Act on them before the next run to keep them
            </p>
          </div>
        </div>

        <div className="overflow-y-auto px-5 py-4 flex flex-col gap-3">
          {cards.map((c) => {
            const p = c.payload;
            const fireAt = p.fireAtApprox ? new Date(p.fireAtApprox) : null;
            const minsLeft = fireAt
              ? Math.max(0, Math.round((fireAt.getTime() - Date.now()) / 60_000))
              : null;
            return (
              <div
                key={c.id}
                className="border border-amber-200 bg-amber-50/40 rounded-[14px] p-3.5 flex flex-col gap-2.5"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[13px] font-semibold text-[#051912] truncate">
                    {p.automationName ?? "Automation"}
                  </span>
                  {minsLeft != null && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10.5px] font-semibold shrink-0">
                      <AlarmClock className="size-2.5" />~{minsLeft} min
                    </span>
                  )}
                </div>

                {p.conditionText && (
                  <div className="flex gap-1.5 bg-white/70 border border-amber-100 rounded-[10px] px-2.5 py-2">
                    <HelpCircle size={12} className="text-amber-600 shrink-0 mt-0.5" />
                    <p className="text-[11.5px] text-[#6E663A] leading-[1.45]">
                      {p.conditionText}
                    </p>
                  </div>
                )}

                <div className="flex flex-col gap-1">
                  {(p.leads ?? []).slice(0, 6).map((l) => (
                    <button
                      key={l.leadId}
                      type="button"
                      onClick={() => openLead(l.leadId)}
                      className="flex items-center gap-1.5 text-[12px] text-left text-[#051912] hover:text-[#156548] transition-colors group"
                    >
                      <ExternalLink className="size-3 text-amber-500 group-hover:text-[#156548] shrink-0" />
                      <span className="font-medium truncate">
                        {l.leadName ?? `Lead #${l.leadId}`}
                      </span>
                    </button>
                  ))}
                  {(p.count ?? 0) > 6 && (
                    <span className="text-[10.5px] text-[#697571]">
                      +{(p.count ?? 0) - 6} more
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-5 py-3.5 border-t border-[#F5F8F7] shrink-0 bg-white">
          <button
            type="button"
            onClick={() => void acknowledge()}
            className="px-3.5 py-1.5 rounded-lg bg-amber-500 text-white text-[12px] font-medium hover:bg-amber-600 transition-colors"
          >
            Got it
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
